import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import { useStepSave } from "../../../../hooks/useStepSave";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import CardBox from "../../CardBox/CardBox";
import { filterVisitaData, getInputNames } from "../../../../utils/formDataHelper";
import { getStringQueryCombosRI } from "../../../../../../data/graphQL/queries/ResumenIngreso/getAllCombosRI";
import { adaptCombosRIArray } from "../../../../../../adapters/resumenIngreso/combosRIAdapter";
import BuscadorInsumo from "./components/BuscadorInsumo";
import useGetInsumos from "../../../../hooks/useGetInsumos";
import { handleChangeImageVisita } from "../../../../utils/handleChangeImageVisita";
import { useDeleteArchivoVisita } from "../../../../hooks/useDeleteArchivoVisita";
import { tipoArchivoVisita } from "../../../../constants/TipoArchivosVisita";
import GET_COMBO_VISITA_VALOR, { adaptCombosVisitas } from "../../../../../../data/graphQL/queries/MisVisitas/getCombosVisitaValor";
import SAVE_VISITA_PARENTERAL, { adaptAddVisitaParenteralInput } from "../../../../../../data/graphQL/mutations/Visitas/saveProgramaParenteral";
import toast from "react-hot-toast";
import { EscalonesGrafico } from "../../../../../../components/EscalonesGrafico";
import useCalculoEscalones from "../../../../../../hooks/useCalculoEscalones";
import { CALCULAR_SCHOFIELD } from "../../../../../../data/graphQL/queries/ResumenIngreso/calcularSchofield";

const COMBO_VISITA_PRODUCTO = 'Producto';
const COMBO_VISITA_MODALIDAD_EXPANSION = 'ModalidadExpansion';
const ID_TIPO_BAP_ESTANDAR = 334;
const ID_TIPO_BAP_MAGISTRAL = 335;

const determinarErrorValidacionBAP = (formData, isContinua, isCiclico) => {
  const validaciones = [
    {
      condition: formData?.bAPEstandar === false && formData?.bAPMagistral === false,
      message: 'Debe seleccionar al menos una indicación.',
    },
    {
      condition: !!formData.bAPEstandar && isContinua && formData.cantidadHorasEstandar < 8,
      message: 'La cantidad de horas de BAP estándar no puede ser menor que 8hs',
    },
    {
      condition: !!formData.bAPEstandar && isContinua && formData.cantidadHorasEstandar > 24,
      message: 'La cantidad de horas de BAP estándar no puede ser mayor que 24hs',
    },
    {
      condition: !!formData.bAPMagistral && isContinua && formData.cantidadHorasMagistral < 8,
      message: 'La cantidad de horas de BAP magistral no puede ser menor que 8hs',
    },
    {
      condition: !!formData.bAPMagistral && isContinua && formData.cantidadHorasMagistral > 24,
      message: 'La cantidad de horas de BAP magistral no puede ser mayor que 24hs',
    },
    {
      condition:
        !!formData.bAPEstandar &&
        isCiclico &&
        (formData.duracionMesetaEstandar < 10 || formData.duracionMesetaEstandar > 20),
      message: 'La duración de la meseta estándar no puede ser menor a 10hs ni mayor a 20hs',
    },
    {
      condition:
        !!formData.bAPMagistral &&
        isCiclico &&
        (formData.duracionMesetaMagistral < 10 || formData.duracionMesetaMagistral > 20),
      message: 'La duración de la meseta magistral no puede ser menor a 10hs ni mayor a 20hs',
    },
  ];

  return validaciones.find((v) => v.condition)?.message;
};

export default function ProgramaParenteral({ readOnly, onSaveRef, onExposeFormatted, combosVisita, complicacionesCombo }) {
  const { deleteArchivo } = useDeleteArchivoVisita();

  const [formData, setFormData] = useState({
    cantidadHorasMagistral: 24,
    cantidadHorasEstandar: 24
  });
  const [combosRI, setCombosRI] = useState();
  const [comboProductoOptions, setComboProductoOptions] = useState([]);
  const [comboModalidadOptions, setComboModalidadOptions] = useState([]);

  // límites para precargar cálculos de requerimientos
  const [limitesRequerimientos, setLimitesRequerimientos] = useState({});

  const datosEscalonesMagistral = useCalculoEscalones({
    volumenBAP: formData?.volumenMagistral,
    duracionMeseta: formData?.duracionMesetaMagistral,
    cantidadEscalones: formData?.cantidadEscalonesMagistral
  });

  const datosEscalonesEstandar = useCalculoEscalones({
    volumenBAP: formData?.volumenEstandar,
    duracionMeseta: formData?.duracionMesetaEstandar,
    cantidadEscalones: formData?.cantidadEscalonesEstandar
  });

  const [loadComboProducto, { data: dataComboProducto }] = useLazyQuery(GET_COMBO_VISITA_VALOR, {
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
    if (!dataComboProducto) return;
    setComboProductoOptions(adaptCombosVisitas(dataComboProducto));
  }, [dataComboProducto]);

  const [loadComboModalidad, { data: dataComboModalidad }] = useLazyQuery(GET_COMBO_VISITA_VALOR, {
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
    if (!dataComboModalidad) return;
    setComboModalidadOptions(adaptCombosVisitas(dataComboModalidad));
  }, [dataComboModalidad]);

  const inputCombos = groups.flatMap((inputgroup) => {
    return inputgroup.inputs.filter((input) => !!input.comboName);
  });

  const { visita, setVisita, saveArchivoVisita } = useVisita();
  const isPediatrico = useMemo(() => {
    return visita?.edad <= 15;
  }, [visita?.edad]);

  const { data: dataCombosRI } = useQuery(getStringQueryCombosRI(inputCombos), {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataCombosRI) return;
    setCombosRI(adaptCombosRIArray(dataCombosRI))
  }, [dataCombosRI]);

  const [getCalculoSchofield] = useLazyQuery(CALCULAR_SCHOFIELD, {
    skip: !isPediatrico
  });

  const [save] = useMutation(SAVE_VISITA_PARENTERAL);

  const {
    insumos,
    nombreInsumoSelected,
    filterStatus,
    insumosLoading,
    setFilterStatus,
    setNombreInsumoSelected
  } = useGetInsumos(visita.idEvaluacion);

  const idComboRIValorModoInfusionMap = useMemo(() => {
    const map = {};
    combosRI?.idComboRIValorModoInfusion.forEach((opt) => {
      if (opt.displayName.toLowerCase().includes("continua")) map.CONTINUA = opt.id;
      if (opt.displayName.toLowerCase().includes("cíclica")) map.CICLICA = opt.id;
    });
    return map;
  }, [combosRI?.idComboRIValorModoInfusion]);

  const { isCiclico, isContinua } = useMemo(() => {
    const isCiclico = formData?.idComboRIValorModoInfusion === idComboRIValorModoInfusionMap.CICLICA;
    const isContinua = formData?.idComboRIValorModoInfusion === idComboRIValorModoInfusionMap.CONTINUA;
    return { isCiclico, isContinua };
  }, [idComboRIValorModoInfusionMap, formData?.idComboRIValorModoInfusion]);

  const actualizarLimitesRequerimiento = (prev, inputName, min, max) => ({
    ...prev,
    [inputName]: { min, max }
  });

  const procesarLimiteRequerimiento = (input) => {
    const { min, max } = isPediatrico ? input.limitePediatrico : input.limiteAdulto;
    setLimitesRequerimientos((prev) => actualizarLimitesRequerimiento(prev, input.name, min, max));
    return { min, max };
  };

  const formattedGroups = useMemo(() => {

    const hasBapMagistral = formData?.bAPMagistral === true;
    const hasBapEstandar = formData?.bAPEstandar === true;
    const hasExpansion = formData?.expansion === true;

    return groups.map((group) => {
      const formattedInputs = group.inputs.map((input) => {
        const newInput = { ...input };

        if (!!newInput.comboName) {
          newInput.options = combosRI?.[input.name];
        }

        switch (input.name) {
          case "expansion":
            newInput.hidden = isContinua;
            break;
          case "idComboVisitaValorProducto":
            newInput.options = comboProductoOptions;
            newInput.hidden = !hasExpansion || isContinua;
            newInput.required = hasExpansion && !isContinua;
            break;
          case "idComboVisitaValorExpansion":
            newInput.options = comboModalidadOptions;
            newInput.hidden = !hasExpansion || isContinua;
            newInput.required = hasExpansion && !isContinua;
            break;
          case "volumen":
            newInput.hidden = !hasExpansion || isContinua;
            newInput.required = hasExpansion && !isContinua;
            break;
          case "flujo":
            newInput.hidden = !hasExpansion || isContinua;
            newInput.required = hasExpansion && !isContinua;
            break;
          case "formulaSchofieldPed":
            newInput.hidden = !isPediatrico;
            break;
          case "justificacionTransitorio":
            const transitorioId = combosRI?.idComboRIValorTipoCateter?.find((c) => c.displayName.toLowerCase() === 'transitorio')?.id;
            newInput.hidden = formData?.idComboRIValorTipoCateter !== transitorioId;
            newInput.required = formData?.idComboRIValorTipoCateter === transitorioId;
            break;
          case "justificacionInsercion":
            newInput.hidden = formData?.insercionNormal !== false;
            newInput.required = formData?.insercionNormal === false;
            break;
          case "volumenMagistral":
            newInput.hidden = !hasBapMagistral;
            break;
          case "cantidadHorasMagistral":
          case "flujoMagistral":
            newInput.hidden = !(hasBapMagistral && isContinua);
            break;
          case "cantidadEscalonesMagistral":
          case "bapMagistralDuracionEscalones":
          case "duracionMesetaMagistral":
          case "cicloMagistral":
          case "graficoEscalonesMagistral":
            newInput.hidden = !(hasBapMagistral && isCiclico);
            break;
          case "diasConexionSemanaMagistral":
            newInput.options = newInput.options.map((o) => ({ ...o, disabled: formData.diasConexionSemanaEstandar?.includes(o.value) }));
            newInput.hidden = !hasBapMagistral;
            break;
          case "idInsumo":
          case "volumenEstandar":
          case "idComboRIValorSellado":
          case "idComboRIValorMotivoEstandar":
          case "justificacionModificacion":
            newInput.hidden = !hasBapEstandar;
            newInput.required = hasBapEstandar;
            break;
          case "diasConexionSemanaEstandar":
            newInput.hidden = !hasBapEstandar;
            newInput.options = newInput.options.map((o) => ({ ...o, disabled: formData.diasConexionSemanaMagistral?.includes(o.value) }));
            break;

          case "cantidadHorasEstandar":
          case "flujoEstandar":
            newInput.hidden = !(hasBapEstandar && isContinua);
            break;
          case "cantidadEscalonesEstandar":
          case "bapEstandarDuracionEscalones":
          case "duracionMesetaEstandar":
          case "cicloEstandar":
          case "graficoEscalonesEstandar":
            newInput.hidden = !(hasBapEstandar && isCiclico);
            break;
          case "calorias":
          case "hidratosCarbono":
          case "proteinas":
          case "lipidos":
            const { min, max } = procesarLimiteRequerimiento(input);
            newInput.min = min;
            newInput.max = max;
            break;
          case "complicaciones":
            const ningunaId = complicacionesCombo?.find((c) => c.displayName.toLowerCase() === 'ninguna')?.id;
            newInput.options = complicacionesCombo?.map((opt) => {
              if (Array.isArray(formData.complicaciones)) {
                const hasNinguna = formData.complicaciones.includes(ningunaId);
                const hasOther = formData.complicaciones.some((id) => id !== ningunaId);
                if (hasNinguna) {
                  return { ...opt, disabled: opt.id !== ningunaId };
                }
                if (hasOther) {
                  return { ...opt, disabled: opt.id === ningunaId };
                }
              }
              return { ...opt, disabled: false };
            });
            break;
          default:
            break;
        }

        if (readOnly) newInput.disabled = true;
        return newInput;
      });

      const hidden = !formattedInputs.some((i) => !i.hidden);
      return { ...group, hidden, inputs: formattedInputs };
    });
  }, [
    readOnly,
    formData?.bAPMagistral,
    formData?.bAPEstandar,
    formData?.expansion,
    formData?.idComboRIValorTipoCateter,
    formData?.insercionNormal,
    formData?.diasConexionSemanaEstandar,
    formData?.diasConexionSemanaMagistral,
    formData?.idInsumo,
    formData.complicaciones,
    combosRI,
    comboProductoOptions,
    comboModalidadOptions,
    complicacionesCombo,
    isCiclico,
    isContinua,
    isPediatrico]
  );

  useStepSave(onSaveRef, async (validate) => {
    const error = determinarErrorValidacionBAP(formData, isContinua, isCiclico);

    if (!!error) {
      toast.error(error);
      const errorSave = new Error(error);
      errorSave.isCustomError = true;
      throw errorSave;
    }
    if (validate) validateStepFields({ formattedGroups, formData });

    const { data } = await save({
      variables: { input: adaptAddVisitaParenteralInput(formData) }
    });

    const idVisitaParenteral = data?.saveVisitaParenteral?.idVisitaParenteral;
    if (!idVisitaParenteral) throw new Error("Error al guardar");

    setVisita((prev) => ({ ...prev, ...formData, idVisitaParenteral }));
  });

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeFile = useCallback((name, fileValue) => {
    handleChangeImageVisita({
      saveArchivoVisita,
      name,
      fileValue,
      idVisita: visita.idVisita,
      handleChange,
      idTipoArchivo: tipoArchivoVisita.LABORATORIO
    });
  }, [visita.idVisita, saveArchivoVisita]);

  const onDeleteImage = (name) => {
    const file = formData[name];
    deleteArchivo({
      idArchivo: file.idArchivo,
      name: name,
      handleChange
    });
  };

  const handleSelectInsumo = (newValue) => {
    handleChange('idInsumo', newValue?.id);
    setNombreInsumoSelected(newValue?.label);
  };

  const calcularFlujoBAP = useCallback((volumen, cantidadHoras) => {
    if (!cantidadHoras || cantidadHoras < 8 || cantidadHoras > 24 || !volumen) return null;
    return Math.ceil(volumen / cantidadHoras);
  }, []);

  const calcularCiclo = useCallback((cantEscalones, duracionMeseta) => {
    if (!cantEscalones || !duracionMeseta || duracionMeseta < 10 || !duracionMeseta > 20) return null;
    return Math.ceil(cantEscalones + duracionMeseta);
  }, []);

  useEffect(() => {
    setFormData((prev) => {

      const {
        volumenMagistral,
        volumenEstandar,
        cantidadHorasMagistral,
        cantidadHorasEstandar,
        cantidadEscalonesMagistral,
        duracionMesetaMagistral,
        cantidadEscalonesEstandar,
        duracionMesetaEstandar
      } = prev;

      //Se respetan los nombres de los campos
      const flujoEstandar = calcularFlujoBAP(volumenEstandar, cantidadHorasEstandar);
      const flujoMagistral = calcularFlujoBAP(volumenMagistral, cantidadHorasMagistral);

      const cicloEstandar = calcularCiclo(cantidadEscalonesEstandar, duracionMesetaEstandar);

      const cicloMagistral = calcularCiclo(cantidadEscalonesMagistral, duracionMesetaMagistral);

      return {
        ...prev,
        flujoMagistral,
        flujoEstandar,
        cicloEstandar,
        cicloMagistral
      };
    });
  }, [
    calcularFlujoBAP,
    calcularCiclo,
    formData.volumenMagistral,
    formData.volumenEstandar,
    formData.cantidadHorasMagistral,
    formData.cantidadHorasEstandar,
    formData.cantidadEscalonesMagistral,
    formData.duracionMesetaMagistral,
    formData.cantidadEscalonesEstandar,
    formData.duracionMesetaEstandar]
  );

  useEffect(() => {

    if (!Array.isArray(combosVisita) || combosVisita.length === 0) return;

    const productoId = combosVisita.find((c) => c.comboName === COMBO_VISITA_PRODUCTO)?.id;
    const modalidadId = combosVisita.find((c) => c.comboName === COMBO_VISITA_MODALIDAD_EXPANSION)?.id;

    if (Number.isInteger(productoId)) {
      loadComboProducto({ variables: { idComboVisita: productoId } });
    }
    if (Number.isInteger(modalidadId)) {
      loadComboModalidad({ variables: { idComboVisita: modalidadId } });
    }
  }, [combosVisita, loadComboProducto, loadComboModalidad]);

  useEffect(() => {
    if (visita) {
      const inputNames = getInputNames(groups, true);
      setFormData({
        ...filterVisitaData(visita, inputNames),
        idVisitaParenteral: visita.idVisitaParenteral,
        diasConexionSemanaEstandar: visita.diasConexion?.filter((d) => d.idComboRIValorTipoBAP === ID_TIPO_BAP_ESTANDAR).map((d) => d.dia) || [],
        diasConexionSemanaMagistral: visita.diasConexion?.filter((d) => d.idComboRIValorTipoBAP === ID_TIPO_BAP_MAGISTRAL).map((d) => d.dia) || [],
        idVisita: visita.idVisita
      });
      calculosRequerimientos();
    }
  }, [visita]);

  const calculosRequerimientos = (() => {
    // si estoy visualizando la visita NO calculo porque ya está guardada y no tienen que mostrarse alterados los valores
    // para no generar confusión
    if (!readOnly)
      setFormData((prev) => {
        let { calorias = 0, proteinas = 0, lipidos = 0, hidratosCarbono = 0 } = prev;
        // valido que estén dentro de los rangos
        if ((calorias < limitesRequerimientos?.calorias.min) || (calorias > limitesRequerimientos?.calorias.max))
          calorias = 0;
        if ((proteinas < limitesRequerimientos?.proteinas.min) || (proteinas > limitesRequerimientos?.proteinas.max))
          proteinas = 0;
        if ((lipidos < limitesRequerimientos?.lipidos.min) || (lipidos > limitesRequerimientos?.lipidos.max))
          lipidos = 0;
        if ((hidratosCarbono < limitesRequerimientos?.hidratosCarbono.min) || (hidratosCarbono > limitesRequerimientos?.hidratosCarbono.max))
          hidratosCarbono = 0;

        return {
          ...prev,
          calorias: calorias,
          caloriasTotales: calorias * visita?.pesoActual,
          proteinas: proteinas,
          proteinasTotales: proteinas * visita?.pesoActual,
          lipidos: lipidos,
          lipidosTotales: lipidos * visita?.pesoActual,
          hidratosCarbono: hidratosCarbono
        };
      });
  });

  useEffect(() => {
    calculosRequerimientos();
  }, [
    formData.calorias,
    formData.proteinas,
    formData.lipidos]
  );

  useEffect(() => {
    const { edad, sexo } = visita;
    const tallaEnMetros = visita?.talla;
    const peso = visita?.pesoActual;

    if (!isPediatrico || edad === null || edad === undefined || !sexo || !tallaEnMetros || !peso) return;

    const getCalculos = async () => {
      const { data } = await getCalculoSchofield({
        variables: {
          tallaEnMetros,
          peso,
          edad,
          sexo
        }
      });

      setVisita((prev) => ({
        ...prev,
        formulaSchofieldPed: data.calcularSchofield
      }));
      setFormData((prev) => ({
        ...prev,
        formulaSchofieldPed: data.calcularSchofield
      }));
    };
    getCalculos();

  }, [
    visita?.talla,
    visita?.pesoActual,
    // visita?.formulaSchofieldPed,
    isPediatrico]
  );

  useEffect(() => {
    onExposeFormatted?.({
      stepId: "programaParenteral",
      stepName: "Programa Parenteral",
      formattedGroups
    });
  }, [onExposeFormatted, formattedGroups]);

  const getFieldKey = (inputName) => {
    if (
      inputName === 'flujoMagistral' ||
      inputName === 'flujoEstandar' ||
      inputName === 'cicloEstandar' ||
      inputName === 'cicloMagistral') {
      return inputName + formData[inputName];
    }

    return inputName;
  };

  return (
    <>
      {formattedGroups.
        filter((group) => !group.hidden).
        map((group, idx) =>
          <CardBox key={idx} title={group.title}>
            <Grid container spacing={2}>
              {group.inputs.
                filter((input) => !input.hidden).
                map((input) =>
                  <Grid size={{ xs: 12 }} key={input.name}>
                    {input.name !== 'idInsumo' &&
                      input.name !== 'graficoEscalonesMagistral' &&
                      input.name !== 'graficoEscalonesEstandar' &&
                      <RenderInputByTypeMobile
                        key={getFieldKey(input.name)}
                        input={input}
                        value={input.fixedValue || formData[input.name]}
                        handleChange={input.type === 'file' ? handleChangeFile : handleChange}
                        handleDelete={input.type === 'file' ? onDeleteImage : null}
                        row={input.type === "multiple_boolean"} />
                    }
                    {input.name === 'idInsumo' && <BuscadorInsumo
                      insumos={insumos}
                      input={input}
                      idInsumo={formData.idInsumo}
                      nombreInsumoSelected={nombreInsumoSelected}
                      filterStatus={filterStatus}
                      setFilterStatus={setFilterStatus}
                      handleSelectInsumo={handleSelectInsumo}
                      insumosLoading={insumosLoading} />
                    }
                    {input.name === 'graficoEscalonesMagistral' &&
                      <EscalonesGrafico
                        {...datosEscalonesMagistral}
                        cantidadEscalones={formData?.cantidadEscalonesMagistral}
                        duracionHoras={formData?.duracionMesetaMagistral} />

                    }
                    {input.name === 'graficoEscalonesEstandar' &&
                      <EscalonesGrafico
                        {...datosEscalonesEstandar}
                        cantidadEscalones={formData?.cantidadEscalonesEstandar}
                        duracionHoras={formData?.duracionMesetaEstandar} />

                    }
                  </Grid>
                )}
            </Grid>
          </CardBox>
        )}
    </>);

}

const groups = [
  {
    title: "",
    inputs: [
      {
        name: "idComboRIValorKarnofsky",
        displayName: "Karnofsky",
        comboName: 'Karnofsky',
        type: "select",
        required: true
      },
      {
        name: "ingestaOral",
        displayName: "Ingesta Oral",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "formulaSchofieldPed",
        displayName: "GER Fórmula de Schofield",
        type: "float",
        required: true,
        disabled: true
      }]

  },
  {
    title: "Accesos",
    inputs: [
      {
        name: "idComboRIValorTipoCateter",
        displayName: "Tipo de catéter",
        comboName: 'Tipo de Catéter AP',
        type: "select",
        required: true
      },
      {
        name: "justificacionTransitorio",
        displayName: "Justificar si es transitorio",
        type: "long_string",
        maxLength: 4000
      },
      {
        name: "fechaColocacion",
        displayName: "Fecha de colocación",
        type: "date",
        disableFuture: true,
        required: true
      },
      {
        name: "idComboRIValorUbicacionCateter",
        displayName: "Ubicación del catéter",
        comboName: 'Ubicación del catéter AP',
        type: "select",
        required: true
      },
      {
        name: "idComboRIValorLumenes",
        displayName: "N° de lúmenes",
        comboName: 'N° de Luces',
        type: "select",
        required: true
      },
      {
        name: "insercionNormal",
        displayName: "¿Sitio de inserción de catéter normal?",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "justificacionInsercion",
        displayName: "Si es NO, justificar",
        type: "long_string",
        maxLength: 4000
      }]

  },
  {
    title: "Indicaciones",
    inputs: [
      {
        name: "idComboRIValorModoInfusion",
        displayName: "Modo de infusión",
        comboName: 'Modo de infusión AP',
        type: "select",
        required: true
      },
      //Magistral
      {
        name: "bAPMagistral",
        displayName: "BAP magistral",
        type: "boolean",
        lightTitle: true,
        required: true,
        onlyWhen: { field: "bAPEstandar", value: false }
      },
      {
        name: "volumenMagistral",
        displayName: "Volumen",
        type: "float",
        required: true,
        min: 1
      },
      {
        name: "diasConexionSemanaMagistral",
        displayName: "Conexiones por semana",
        required: true,
        type: 'checkbox',
        options: [
          { value: 1, label: 'L' },
          { value: 2, label: 'M' },
          { value: 3, label: 'X' },
          { value: 4, label: 'J' },
          { value: 5, label: 'V' },
          { value: 6, label: 'S' },
          { value: 7, label: 'D' }],

        lightTitle: true,
        selectAllOption: true
      },
      {
        name: "cantidadHorasMagistral",
        displayName: "Cantidad de horas (continuo)",
        type: "int",
        disabled: true
      },
      {
        name: "flujoMagistral",
        displayName: "Flujo BAP Magistral (ml/h)",
        type: "int",
        shrink: "true",
        disabled: true
      },
      {
        name: "cantidadEscalonesMagistral",
        displayName: "Cantidad de escalones",
        type: "select",
        options: [{ id: 1, displayName: "1" }, { id: 2, displayName: "2" }],
        required: true
      },
      {
        name: "bapMagistralDuracionEscalones",
        displayName: "Duracion de escalones",
        type: "infoRow",
        fixedValue: '30 minutos'
      },
      {
        name: "duracionMesetaMagistral",
        displayName: "Duración de meseta (horas)",
        type: "int",
        required: true,
        max: 20
      },
      {
        name: "cicloMagistral",
        displayName: "Ciclo (horas)",
        type: "int",
        shrink: 'true',
        disabled: true
      },
      {
        name: 'graficoEscalonesMagistral',
        displaName: '',
        type: 'custom'
      },
      //Estandar
      {
        name: "bAPEstandar",
        displayName: "BAP estándar",
        type: "boolean",
        lightTitle: true,
        required: true,
        onlyWhen: { field: "bAPMagistral", value: false }
      },
      {
        name: "idInsumo",
        displayName: "¿Cuál?",
        type: "search"
      },
      {
        name: "volumenEstandar",
        displayName: "Volumen",
        type: "float",
        required: true,
        min: 1
      },
      {
        name: "idComboRIValorMotivoEstandar",
        displayName: "Motivo de la indicación estándar",
        comboName: 'Motivo de la indicación Estandar AP',
        type: "select",
        required: false
      },
      {
        name: "diasConexionSemanaEstandar",
        displayName: "Conexiones x semana",
        required: true,
        type: 'checkbox',
        options: [
          { value: 1, label: 'L' },
          { value: 2, label: 'M' },
          { value: 3, label: 'X' },
          { value: 4, label: 'J' },
          { value: 5, label: 'V' },
          { value: 6, label: 'S' },
          { value: 7, label: 'D' }],

        lightTitle: true,
        selectAllOption: true
      },
      {
        name: "justificacionModificacion",
        displayName: "Justificación por modificación",
        type: "long_string",
        maxLength: 4000
      },
      {
        name: "cantidadHorasEstandar",
        displayName: "Cantidad de horas (continuo)",
        type: "int",
        disabled: true
      },
      {
        name: "flujoEstandar",
        displayName: "Flujo BAP Estándar (ml/h)",
        type: "int",
        shrink: "true",
        disabled: true
      },
      {
        name: "cantidadEscalonesEstandar",
        displayName: "Cantidad de escalones",
        type: "select",
        options: [{ id: 1, displayName: "1" }, { id: 2, displayName: "2" }],
        required: true
      },
      {
        name: "bapEstandarDuracionEscalones",
        displayName: "Duracion de escalones",
        type: "infoRow",
        fixedValue: '30 minutos'
      },
      {
        name: "duracionMesetaEstandar",
        displayName: "Duración de meseta (horas)",
        type: "int",
        required: true,
        max: 20
      },
      {
        name: "cicloEstandar",
        displayName: "Ciclo (horas)",
        type: "int",
        shrink: "true",
        disabled: true
      },
      {
        name: 'graficoEscalonesEstandar',
        displaName: '',
        type: 'custom'
      },
      {
        displayName: 'Sellado de catéter',
        name: 'idComboRIValorSellado',
        comboName: 'Sellado de catéter AP',
        type: 'select',
        required: true
      },

      //Fin Estandar
      {
        name: "expansion",
        displayName: "Expansión",
        type: "boolean",
        lightTitle: true
      },
      {
        name: "idComboVisitaValorProducto",
        displayName: "Producto",
        type: "select"
      },
      {
        name: "volumen",
        displayName: "Volumen (ml)",
        type: "int",
        min: 0
      },
      {
        name: "idComboVisitaValorExpansion",
        displayName: "Modalidad de expansión",
        type: "select"
      },
      {
        name: "flujo",
        displayName: "Flujo",
        type: "string",
        maxLength: 500
      },
      {
        name: "indicacionComentario",
        displayName: "Comentarios de indicación",
        type: "long_string",
        maxLength: 4000
      },
      {
        name: "indicacionEspecial",
        displayName: "Indicación especial",
        type: "long_string",
        maxLength: 1000
      }]

  },
  {
    title: "Requerimientos",
    inputs: [
      {
        name: "calorias",
        displayName: "Calorías/kg/d",
        type: "float",
        required: true,
        limiteAdulto: {
          min: 1,
          max: 50
        },
        limitePediatrico: {
          min: 0,
          max: 105
        }
      },
      {
        name: "hidratosCarbono",
        displayName: "Hidratos de Carbono/kg/d",
        type: "float",
        required: true,
        limiteAdulto: {
          min: 1,
          max: 7
        },
        limitePediatrico: {
          min: 0,
          max: 19
        }

      },
      {
        name: "proteinas",
        displayName: "Proteínas/kg/d",
        type: "float",
        required: true,
        limiteAdulto: {
          min: 0,
          max: 2.5
        },
        limitePediatrico: {
          min: 0,
          max: 3.5
        }
      },
      {
        name: "lipidos",
        displayName: "Lípidos/kg/d",
        type: "float",
        required: true,
        limiteAdulto: {
          min: 0,
          max: 2
        },
        limitePediatrico: {
          min: 0,
          max: 3.5
        }
      },
      {
        name: "proteinasTotales",
        displayName: "Proteínas totales/d",
        type: "float",
        disabled: true

      },
      {
        name: "lipidosTotales",
        displayName: "Lípidos totales/d",
        type: "float",
        disabled: true
      },
      {
        name: "caloriasTotales",
        displayName: "Calorías totales/d",
        type: "float",
        disabled: true
      },
      {
        name: "laboratorioAdjunto",
        displayName: "Laboratorio (adjuntar)",
        type: "file"
      },
      {
        name: "complicaciones",
        displayName: "Complicaciones",
        type: "multiple_select",
        required: true
      }]

  }];